// EL MENSAJE DEL KIT DE CAMBIO DE VIDA, EN EL IDIOMA ELEGIDO (E-420).
//
// Josef Correia (Allinsure Group), 2026-11-03: *"Once you edit the message,
// then you toggle spanish or english it keeps the language from the original
// message. So I have to keep editing between spanish and english clients."*
//
// El mensaje de la agencia era UNO por tipo de seguro, sin idioma. Mientras no
// se editaba, el botón de idioma cambiaba entre los dos textos de fábrica; en
// cuanto se guardaba uno propio, salía ese en los dos idiomas.
//
// Ahora cada mensaje tiene su versión en cada idioma, con la misma convención
// que los demás kits (crm-app/kitPorContexto.js): la llave de siempre es la
// versión principal y la inglesa va al lado con `__en`
// (`life_change__HEALTH__en`).
//
// Lo que ya estaba guardado no tenía idioma. Para no perderlo, se mira en qué
// idioma está escrito: si está en inglés, cuenta como la versión inglesa y el
// español vuelve al texto de fábrica (y al revés). Así el mensaje que Josef ya
// escribió sigue saliendo, pero solo a los clientes de su idioma.

import { KIT_LIFE_CHANGE, lifeChangeKitKey } from './policyTypes.js';

const SUFIJO_EN = '__en';

const PALABRAS = {
  es: ['el', 'la', 'los', 'las', 'de', 'del', 'que', 'y', 'su', 'sus', 'usted', 'para', 'por', 'con', 'hola', 'gracias',
    'póliza', 'poliza', 'cambio', 'vida', 'cualquier', 'favor', 'nos', 'le', 'una', 'un', 'es', 'en', 'si', 'cobertura'],
  en: ['the', 'and', 'your', 'you', 'to', 'of', 'for', 'with', 'hello', 'hi', 'thank', 'thanks', 'policy', 'please',
    'life', 'change', 'any', 'we', 'our', 'is', 'in', 'if', 'a', 'an', 'coverage', 'let', 'us', 'know'],
};

/** 'es' o 'en' según las palabras más comunes del texto (sin HTML ni {campos}). Empate o vacío: 'es'. */
export function idiomaDelTexto(texto) {
  const limpio = String(texto || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\{[^}]*\}/g, ' ')
    .replace(/&[a-z]+;/gi, ' ')
    .toLowerCase();
  const palabras = limpio.match(/[a-záéíóúñü]+/g) || [];
  let es = 0;
  let en = 0;
  for (const p of palabras) {
    if (PALABRAS.es.includes(p)) es += 1;
    if (PALABRAS.en.includes(p)) en += 1;
  }
  return en > es ? 'en' : 'es';
}

const lleno = (t) => !!(t && typeof t === 'object'
  && ((t.subject && String(t.subject).trim()) || (t.body && String(t.body).trim())));

/** La versión de UNA llave (la de un tipo, o la General) en ese idioma, o null. */
function versionDeLlave(mapa, llave, idioma) {
  if (idioma === 'en' && lleno(mapa[llave + SUFIJO_EN])) return mapa[llave + SUFIJO_EN];
  const principal = mapa[llave];
  if (!lleno(principal)) return null;
  // La principal sirve para el idioma en que está escrita. Si ya existe la
  // versión inglesa aparte, la principal es la española aunque parezca inglés.
  const suIdioma = lleno(mapa[llave + SUFIJO_EN]) ? 'es' : idiomaDelTexto(`${principal.subject || ''} ${principal.body || ''}`);
  return suIdioma === idioma ? principal : null;
}

/**
 * El mensaje que se usa: el del tipo en ese idioma; si no hay, el General en
 * ese idioma; si tampoco, null (y la pantalla pone el texto de fábrica de ese
 * idioma).
 */
export function mensajeDeCambioDeVida(mapa, tipo, idioma) {
  const m = mapa && typeof mapa === 'object' ? mapa : {};
  return versionDeLlave(m, lifeChangeKitKey(tipo), idioma)
    || (lifeChangeKitKey(tipo) !== KIT_LIFE_CHANGE ? versionDeLlave(m, KIT_LIFE_CHANGE, idioma) : null);
}

/** Lo que se abre al editar: la versión de ESTE tipo en ESTE idioma (vacía si no hay). */
export function versionParaEditar(mapa, tipo, idioma) {
  const m = mapa && typeof mapa === 'object' ? mapa : {};
  return versionDeLlave(m, lifeChangeKitKey(tipo), idioma) || { subject: '', body: '' };
}

/**
 * El mapa con la versión de este tipo y este idioma cambiada. Vacía = se borra.
 *
 * Si se guarda el español y la principal que había estaba escrita en inglés
 * (un mensaje de antes, sin idioma), esa se pasa primero a la versión inglesa:
 * escribir el español encima la borraría.
 */
export function mapaConVersion(mapa, tipo, idioma, valor) {
  const siguiente = { ...(mapa && typeof mapa === 'object' ? mapa : {}) };
  const llave = lifeChangeKitKey(tipo);
  const principal = siguiente[llave];
  if (idioma === 'es' && lleno(principal) && !lleno(siguiente[llave + SUFIJO_EN])
    && idiomaDelTexto(`${principal.subject || ''} ${principal.body || ''}`) === 'en') {
    siguiente[llave + SUFIJO_EN] = principal;
    delete siguiente[llave];
  }
  const destino = idioma === 'en' ? llave + SUFIJO_EN : llave;
  if (lleno(valor)) siguiente[destino] = { subject: String(valor.subject || ''), body: String(valor.body || '') };
  else delete siguiente[destino];
  // Una principal inglesa de antes que ya tiene su versión inglesa guardada
  // aparte no debe seguir contando como española.
  if (idioma === 'en' && lleno(principal) && !lleno(mapa?.[llave + SUFIJO_EN])
    && idiomaDelTexto(`${principal.subject || ''} ${principal.body || ''}`) === 'en') {
    delete siguiente[llave];
  }
  return siguiente;
}

/** ¿Hay mensaje propio (de este tipo o del General) en este idioma? */
export const hayMensajePropio = (mapa, tipo, idioma) => !!mensajeDeCambioDeVida(mapa, tipo, idioma);
